import { useEffect } from "react";
import {
  AuthBindings,
  Authenticated,
  ErrorComponent,
  Refine,
} from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbarProvider } from "@refinedev/kbar";
import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { generateDataProvider } from "./providers/dataProvider";
import axios from "axios";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import "./App.css";
import { Layout } from "./components/layout";
import { Login } from "./pages/login";
import IMDFListPage from "./pages/imdf/list";
import IMDFDetailPage from "./pages/imdf/detail";
import IMDFEditorWithFloors from "./pages/imdf/editor-with-floors";
import ProjectListPage from "./pages/project/list";
import ProjectDetailPage from "./pages/project/detail";
import UserListPage from "./pages/users/list";
import UserCreatePage from "./pages/users/create";
import UserDetailPage from "./pages/users/detail";
import UserEditPage from "./pages/users/edit";
import AdminEditUserPage from "./pages/users/admin-edit";
import UserProfilePage from "./pages/users/profile";
import ViewListPage from "./pages/view/list";
import ViewDetailPage from "./pages/view/detail";
import ReviewDashboard from "./pages/proposals/review-dashboard";
import ReviewDetailPage from "./pages/proposals/review-detail";
import IMDFEditorListPage from "./pages/imdf-editor/list";
import IMDFEditorEditPage from "./pages/imdf-editor/edit";
import { PermissionGuard } from "./components/PermissionGuard";

const axiosInstance = axios.create();
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (config.headers) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});
const API_URL = import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
function App() {
  const authProvider: AuthBindings = {
    login: async (params: any) => {
      const { email, password, loginType } = params;

      // Google OAuth 登入
      if (!loginType || loginType === "google") {
        window.location.href = `${import.meta.env.VITE_API_URL}/auth/google`;
        return {
          success: true,
        };
      }

      // 帳號密碼登入
      if (loginType === "password") {
        try {
          const API_URL =
            import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
          const response = await fetch(`${API_URL}/auth/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              success: false,
              error: {
                message: data.message || "登入失敗",
                name: "LoginError",
              },
            };
          }

          // 儲存 tokens
          localStorage.setItem("token", data.data.accessToken);
          localStorage.setItem("refreshToken", data.data.refreshToken);
          localStorage.setItem("user", JSON.stringify(data.data.user));

          return {
            success: true,
            redirectTo: "/",
          };
        } catch (error) {
          return {
            success: false,
            error: {
              message: "網路錯誤，請稍後再試",
              name: "NetworkError",
            },
          };
        }
      }

      return {
        success: false,
        error: {
          message: "不支援的登入方式",
          name: "UnsupportedLoginType",
        },
      };
    },
    logout: async () => {
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          const API_URL =
            import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
          await fetch(`${API_URL}/auth/logout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken }),
          });
        }
      } catch (error) {
        console.error("Logout error:", error);
      }

      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");

      return {
        success: true,
        redirectTo: "/login",
      };
    },
    onError: async (error) => {
      console.error(error);

      // 如果是 token 過期錯誤，嘗試刷新 token
      if (error?.response?.status === 401) {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          try {
            const API_URL =
              import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
            const response = await fetch(`${API_URL}auth/refresh`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ refreshToken }),
            });

            if (response.ok) {
              const data = await response.json();
              localStorage.setItem("token", data.data.accessToken);
              localStorage.setItem("refreshToken", data.data.refreshToken);
              localStorage.setItem("user", JSON.stringify(data.data.user));

              // 重新執行原始請求
              window.location.reload();
              return {};
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
          }
        }

        // Token 刷新失敗，登出用戶
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");

        return {
          logout: true,
          redirectTo: "/login",
          error: {
            message: "登入已過期，請重新登入",
            name: "TokenExpired",
          },
        };
      }

      return { error };
    },
    check: async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          // 檢查 token 是否過期
          const payload = JSON.parse(atob(token.split(".")[1]));
          const currentTime = Date.now() / 1000;

          if (payload.exp && payload.exp < currentTime) {
            // Token 過期，嘗試刷新
            const refreshToken = localStorage.getItem("refreshToken");
            if (refreshToken) {
              try {
                const API_URL =
                  import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
                const response = await fetch(`${API_URL}auth/refresh`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ refreshToken }),
                });

                if (response.ok) {
                  const data = await response.json();
                  localStorage.setItem("token", data.data.accessToken);
                  localStorage.setItem("refreshToken", data.data.refreshToken);
                  localStorage.setItem("user", JSON.stringify(data.data.user));

                  return {
                    authenticated: true,
                  };
                }
              } catch (error) {
                console.error("Token refresh failed:", error);
              }
            }

            // 刷新失敗，清除 tokens
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");

            return {
              authenticated: false,
              logout: true,
              redirectTo: "/login",
            };
          }

          return {
            authenticated: true,
          };
        } catch (error) {
          console.error("Token validation error:", error);
          return {
            authenticated: false,
            logout: true,
            redirectTo: "/login",
          };
        }
      }
      return {
        authenticated: false,
        logout: true,
        redirectTo: "/login",
      };
    },
    getPermissions: async () => {
      const user = localStorage.getItem("user");
      if (user) {
        try {
          const userData = JSON.parse(user);
          return userData.role || "user";
        } catch (error) {
          console.error("Error parsing user data:", error);
        }
      }
      return null;
    },
    getIdentity: async () => {
      const user = localStorage.getItem("user");
      if (user) {
        try {
          return JSON.parse(user);
        } catch (error) {
          console.error("Error parsing user data:", error);
          // Fallback: 從 token 中解析
          const token = localStorage.getItem("token");
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split(".")[1]));
              return payload.user;
            } catch (tokenError) {
              console.error("Error parsing token:", tokenError);
            }
          }
        }
      }
      return null;
    },
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const refreshToken = params.get("refreshToken");

    if (token) {
      localStorage.setItem("token", token);

      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      // 從 token 中解析用戶資料並儲存
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.user) {
          localStorage.setItem("user", JSON.stringify(payload.user));
        }
      } catch (error) {
        console.error("Error parsing token for user data:", error);
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <DevtoolsProvider>
          <Refine
            dataProvider={generateDataProvider(API_URL)}
            routerProvider={routerBindings}
            authProvider={authProvider}
            resources={[
              {
                name: "IMDF",
                list: "/imdf",
                meta: {
                  canDelete: false,
                  label: "IMDF 管理",
                },
              },
              {
                name: "IMDF Editor",
                list: "/imdf-editor",
                meta: {
                  canDelete: false,
                  label: "編輯 IMDF",
                },
              },
              {
                name: "Project",
                list: "/projects",
                meta: {
                  canDelete: false,
                },
              },
              {
                name: "User",
                list: "/users",
                create: "/users/create",
                show: "/users/:id",
                meta: {
                  canDelete: false,
                },
              },
              {
                name: "View",
                list: "/view",
                meta: {
                  canDelete: false,
                  label: "View",
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated
                    key="authenticated-root"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <Layout>
                      <Outlet />
                    </Layout>
                  </Authenticated>
                }
              >
                <Route index element={<NavigateToResource resource="imdf" />} />
                {/* 專案路由 */}
                <Route path="/projects">
                  <Route index element={<ProjectListPage />} />
                  <Route path=":id" element={<ProjectDetailPage />} />
                </Route>

                {/* IMDF 編輯器路由 - 僅限 admin/maintainer */}
                <Route path="/imdf-editor">
                  <Route
                    index
                    element={
                      <PermissionGuard
                        requiredRoles={["admin", "maintainer"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取 IMDF 編輯功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <IMDFEditorListPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="edit/:id"
                    element={
                      <PermissionGuard
                        requiredRoles={["admin", "maintainer"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取 IMDF 編輯功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <IMDFEditorEditPage />
                      </PermissionGuard>
                    }
                  />
                </Route>

                {/* IMDF 檔案路由 - 僅限 admin/maintainer */}
                <Route path="/imdf">
                  <Route
                    index
                    element={
                      <PermissionGuard
                        requiredRoles={["admin", "maintainer"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取 IMDF 管理功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <IMDFListPage />
                      </PermissionGuard>
                    }
                  />
                  <Route 
                    path="view/:id" 
                    element={
                      <PermissionGuard 
                        requiredRoles={["admin", "maintainer"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取 IMDF 管理功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <IMDFDetailPage />
                      </PermissionGuard>
                    } 
                  />
                  <Route 
                    path="edit/:id" 
                    element={
                      <PermissionGuard 
                        requiredRoles={["admin", "maintainer"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取 IMDF 管理功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <IMDFEditorWithFloors />
                      </PermissionGuard>
                    } 
                  />
                </Route>

                {/* 用戶管理路由 - 僅限 admin */}
                <Route path="/users">
                  <Route
                    index
                    element={
                      <PermissionGuard
                        requiredRoles={["admin"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取用戶管理功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <UserListPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <PermissionGuard requiredRoles={["admin"]}>
                        <UserCreatePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      <PermissionGuard requiredRoles={["admin"]}>
                        <UserDetailPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path=":id/edit"
                    element={
                      <PermissionGuard requiredRoles={["admin"]}>
                        <UserEditPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path=":id/admin-edit"
                    element={
                      <PermissionGuard requiredRoles={["admin"]}>
                        <AdminEditUserPage />
                      </PermissionGuard>
                    }
                  />
                </Route>

                {/* Profile 路由 - 僅限 user/maintainer */}
                <Route
                  path="/profile"
                  element={
                    <PermissionGuard
                      requiredRoles={["user", "maintainer"]}
                      fallback={
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                            <p className="mt-1 text-sm text-gray-500">您沒有權限存取個人資料頁面</p>
                            <div className="mt-6">
                              <button
                                type="button"
                                onClick={() => window.location.href = "/view"}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                              >
                                前往檔案瀏覽
                              </button>
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <UserProfilePage />
                    </PermissionGuard>
                  }
                />

                {/* View 路由 */}
                <Route path="/view">
                  <Route index element={<ViewListPage />} />
                  <Route path=":id" element={<ViewDetailPage />} />
                </Route>

                {/* Proposal 審核路由 - 僅限 admin */}
                <Route path="/proposals">
                  <Route 
                    path="review" 
                    element={
                      <PermissionGuard 
                        requiredRoles={["admin"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取提案審核功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <ReviewDashboard />
                      </PermissionGuard>
                    } 
                  />
                  <Route 
                    path="review/:proposalId" 
                    element={
                      <PermissionGuard 
                        requiredRoles={["admin"]}
                        fallback={
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒絕</h3>
                              <p className="mt-1 text-sm text-gray-500">您沒有權限存取提案審核功能</p>
                              <div className="mt-6">
                                <button
                                  type="button"
                                  onClick={() => window.location.href = "/view"}
                                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  前往檔案瀏覽
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <ReviewDetailPage />
                      </PermissionGuard>
                    } 
                  />
                </Route>

                <Route path="*" element={<ErrorComponent />} />
              </Route>
              <Route
                element={
                  <Authenticated
                    key="authenticated-login"
                    fallback={<Outlet />}
                  >
                    <NavigateToResource />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<Login />} />
              </Route>
            </Routes>
            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
          <DevtoolsPanel />
        </DevtoolsProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
